import { ConflictError, NotFoundError } from '@domain/errors';
import type {
  Address,
  AddressDraft,
  AddressImportRow,
  AddressReview,
  AdminAccount,
  AdminDraft,
  AppUser,
  AppUserDraft,
  FlaggedReview,
} from '@domain/entities';
import type {
  AddressQuery,
  AddressRepository,
  AdminRepository,
  AppUserQuery,
  AppUserRepository,
  ReviewRepository,
} from '@domain/repositories';
import { buildPage } from '@domain/value-objects';
import type { AccountStatus, Page } from '@domain/value-objects';
import { SEED_IMPORT_PREVIEW, defaultRights } from '../../seed/dataset';
import { InMemoryDb, TODAY_LABEL, latency, matches } from './in-memory-db';

export class InMemoryAddressRepository implements AddressRepository {
  constructor(private readonly db: InMemoryDb) {}

  async search(query: AddressQuery): Promise<Page<Address>> {
    const filtered = this.db.addresses.filter(
      (a) => matches(a.name, query.search) || matches(a.city, query.search),
    );
    return latency(buildPage(filtered, query));
  }

  async create(draft: AddressDraft): Promise<Address> {
    const created: Address = {
      ...draft,
      id: this.db.nextId('ad'),
      rating: 0,
      reviewsCount: 0,
      publishedAt: null,
    };
    this.db.addresses = [created, ...this.db.addresses];
    return latency(created, 140);
  }

  async update(id: string, draft: AddressDraft): Promise<Address> {
    const index = this.db.addresses.findIndex((a) => a.id === id);
    if (index === -1) throw new NotFoundError('Adresse', id);
    const updated: Address = { ...this.db.addresses[index], ...draft };
    this.db.addresses[index] = updated;
    return latency(updated, 120);
  }

  async remove(ids: readonly string[]): Promise<void> {
    const idSet = new Set(ids);
    const before = this.db.addresses.length;
    this.db.addresses = this.db.addresses.filter((a) => !idSet.has(a.id));
    if (before - this.db.addresses.length !== idSet.size) {
      throw new NotFoundError('Adresse', ids.join(','));
    }
    await latency(null, 120);
  }

  async setActive(ids: readonly string[], active: boolean): Promise<Address[]> {
    const idSet = new Set(ids);
    const updated: Address[] = [];
    this.db.addresses = this.db.addresses.map((a) => {
      if (!idSet.has(a.id)) return a;
      const next: Address = { ...a, publishedAt: active ? TODAY_LABEL : null };
      updated.push(next);
      return next;
    });
    return latency(updated, 120);
  }

  async listReviews(addressId: string): Promise<AddressReview[]> {
    return latency(this.db.addressReviews.filter((r) => r.addressId === addressId).map(
      (r): AddressReview => ({
        id: r.id,
        author: r.author,
        rating: r.rating,
        comment: r.comment,
        createdAt: r.createdAt,
      }),
    ), 80);
  }

  async deleteReview(reviewId: string): Promise<void> {
    const index = this.db.addressReviews.findIndex((r) => r.id === reviewId);
    if (index === -1) throw new NotFoundError('Avis', reviewId);
    const { addressId } = this.db.addressReviews[index];
    this.db.addressReviews = this.db.addressReviews.filter((r) => r.id !== reviewId);
    const remaining = this.db.addressReviews.filter((r) => r.addressId === addressId);
    const avg = remaining.length > 0
      ? remaining.reduce((s, r) => s + r.rating, 0) / remaining.length
      : 0;
    this.db.addresses = this.db.addresses.map((a) =>
      a.id === addressId
        ? { ...a, rating: Math.round(avg * 10) / 10, reviewsCount: remaining.length }
        : a,
    );
    await latency(null, 100);
  }

  async parseImportFile(file: File): Promise<AddressImportRow[]> {
    if (file.size === 0) throw new ConflictError('Fichier vide.');
    return latency([...SEED_IMPORT_PREVIEW], 400);
  }

  async importRows(rows: readonly AddressImportRow[]): Promise<Address[]> {
    const created = rows.map(
      (row): Address => ({
        id: this.db.nextId('imp'),
        name: row.name,
        category: row.category,
        city: row.city,
        address: '',
        phone: row.phone,
        whatsapp: row.phone,
        rating: 0,
        reviewsCount: 0,
        publishedAt: null,
      }),
    );
    this.db.addresses = [...created, ...this.db.addresses];
    return latency(created, 260);
  }
}

export class InMemoryReviewRepository implements ReviewRepository {
  constructor(private readonly db: InMemoryDb) {}

  async listFlagged(): Promise<FlaggedReview[]> {
    return latency([...this.db.reviews], 120);
  }

  async countFlagged(): Promise<number> {
    return this.db.reviews.length;
  }

  async dismiss(id: string): Promise<void> {
    this.assertExists(id);
    this.db.reviews = this.db.reviews.filter((r) => r.id !== id);
    await latency(null, 100);
  }

  async remove(id: string): Promise<void> {
    this.assertExists(id);
    this.db.reviews = this.db.reviews.filter((r) => r.id !== id);
    await latency(null, 100);
  }

  private assertExists(id: string): void {
    if (!this.db.reviews.some((r) => r.id === id)) throw new NotFoundError('Avis', id);
  }
}

function computePregnancy(dueDate: string): string {
  if (!dueDate) return '';
  const diffDays = Math.round((new Date(dueDate).getTime() - Date.now()) / 86400000);
  const week = Math.round(40 - diffDays / 7);
  if (week > 40) return 'Post-partum';
  if (week < 1) return '';
  return `${week} SA`;
}

export class InMemoryAppUserRepository implements AppUserRepository {
  constructor(private readonly db: InMemoryDb) {}

  async search(query: AppUserQuery): Promise<Page<AppUser>> {
    const filtered = this.db.users.filter(
      (u) =>
        (query.status === 'all' || u.status === query.status) &&
        (matches(u.name, query.search) || matches(u.email, query.search)),
    );
    return latency(buildPage(filtered, query));
  }

  async findById(id: string): Promise<AppUser> {
    const found = this.db.users.find((u) => u.id === id);
    if (!found) throw new NotFoundError('Utilisatrice', id);
    return latency(found, 60);
  }

  async countByStatus(status: AccountStatus): Promise<number> {
    return this.db.users.filter((u) => u.status === status).length;
  }

  async create(draft: AppUserDraft): Promise<AppUser> {
    const id = `u_${Date.now()}`;
    const name = draft.name || draft.email || draft.phone || '—';
    const created: AppUser = {
      ...draft,
      id,
      name,
      initials: name.slice(0, 2).toUpperCase(),
      avatarUrl: '',
      pregnancy: computePregnancy(draft.dueDate),
      status: 'active',
      registeredAt: new Date().toISOString(),
    };
    this.db.users.unshift(created);
    return latency(created, 120);
  }

  async update(id: string, draft: AppUserDraft): Promise<AppUser> {
    const index = this.db.users.findIndex((u) => u.id === id);
    if (index === -1) throw new NotFoundError('Utilisatrice', id);
    const updated: AppUser = {
      ...this.db.users[index],
      ...draft,
      pregnancy: computePregnancy(draft.dueDate),
    };
    this.db.users[index] = updated;
    return latency(updated, 120);
  }

  async setStatus(id: string, status: AccountStatus): Promise<AppUser> {
    const index = this.db.users.findIndex((u) => u.id === id);
    if (index === -1) throw new NotFoundError('Utilisatrice', id);
    const updated: AppUser = { ...this.db.users[index], status };
    this.db.users[index] = updated;
    return latency(updated, 120);
  }

  async exportAll(): Promise<AppUser[]> {
    return latency([...this.db.users], 200);
  }

  async remove(id: string): Promise<void> {
    const index = this.db.users.findIndex((u) => u.id === id);
    if (index === -1) throw new NotFoundError('Utilisatrice', id);
    this.db.users.splice(index, 1);
    return latency(undefined as void, 120);
  }
}

export class InMemoryAdminRepository implements AdminRepository {
  constructor(private readonly db: InMemoryDb) {}

  async list(): Promise<AdminAccount[]> {
    return latency([...this.db.admins], 120);
  }

  async findById(id: string): Promise<AdminAccount> {
    const found = this.db.admins.find((a) => a.id === id);
    if (!found) throw new NotFoundError('Admin', id);
    return latency(found, 60);
  }

  async create(draft: AdminDraft): Promise<AdminAccount> {
    if (this.db.admins.some((a) => a.email === draft.email)) {
      throw new ConflictError('Un compte utilise déjà cet email.');
    }
    const created: AdminAccount = {
      id: this.db.nextId('admin'),
      name: draft.name,
      email: draft.email,
      role: draft.role,
      status: 'active',
      rights: draft.role !== 'SUPER_ADMIN' ? draft.rights : undefined,
    };
    this.db.admins = [created, ...this.db.admins];
    return latency(created, 140);
  }

  async update(id: string, draft: AdminDraft): Promise<AdminAccount> {
    const index = this.db.admins.findIndex((a) => a.id === id);
    if (index === -1) throw new NotFoundError('Admin', id);
    const updated: AdminAccount = {
      ...this.db.admins[index],
      name: draft.name,
      email: draft.email,
      role: draft.role,
      rights: draft.role !== 'SUPER_ADMIN' ? draft.rights : undefined,
    };
    this.db.admins[index] = updated;
    return latency(updated, 120);
  }

  async setStatus(id: string, status: AccountStatus): Promise<AdminAccount> {
    const index = this.db.admins.findIndex((a) => a.id === id);
    if (index === -1) throw new NotFoundError('Admin', id);
    const updated: AdminAccount = { ...this.db.admins[index], status };
    this.db.admins[index] = updated;
    return latency(updated, 120);
  }

  async toggleRole(id: string): Promise<AdminAccount> {
    const index = this.db.admins.findIndex((a) => a.id === id);
    if (index === -1) throw new NotFoundError('Admin', id);
    const current = this.db.admins[index];
    const nextRole = current.role === 'SUPER_ADMIN' ? 'ADMIN' : 'SUPER_ADMIN';
    const updated: AdminAccount = {
      ...current,
      role: nextRole,
      rights: nextRole !== 'SUPER_ADMIN' ? (current.rights ?? defaultRights()) : undefined,
    };
    this.db.admins[index] = updated;
    return latency(updated, 120);
  }
}

export { TODAY_LABEL };
